import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Avatar
} from '@mui/material';
import {
  ArrowBack,
  Restaurant as RestaurantIcon,
  Chat as ChatIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';
import { restaurantApi, handleApiError } from '../services/api';
import ChatBot from '../components/ChatBot';
import type { Restaurant } from '../types';

const ChatPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [chatOpen, setChatOpen] = useState(true);

  useEffect(() => {
    if (id) {
      loadRestaurant(id);
    }
  }, [id]);

  const loadRestaurant = async (restaurantId: string) => {
    try {
      setLoading(true);
      const data = await restaurantApi.getRestaurant(restaurantId);
      setRestaurant(data);
      setError('');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading restaurant...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !restaurant) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error || 'Restaurant not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Restaurants
        </Button>
        
        <Paper elevation={2} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <RestaurantIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="h3" gutterBottom>
                {restaurant.name}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                {restaurant.cuisine_type} Cuisine
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>
                {restaurant.description}
              </Typography>
            </Box>
            <Box textAlign="center">
              <BotIcon sx={{ fontSize: 60, mb: 1 }} />
              <Typography variant="h6">
                AI Menu Assistant
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Ready to help!
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Chat Instructions */}
      <Box mb={4}>
        <Card elevation={1}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Start Your Conversation
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Our AI assistant knows everything about {restaurant.name}'s menu. You can ask about:
            </Typography>
            <Box display="flex" gap={4} flexWrap="wrap">
              <Box flex={1} minWidth="250px">
                <Box component="ul" sx={{ pl: 2 }}>
                  <li>Menu recommendations</li>
                  <li>Ingredients and allergens</li>
                  <li>Dietary restrictions</li>
                  <li>Popular dishes</li>
                </Box>
              </Box>
              <Box flex={1} minWidth="250px">
                <Box component="ul" sx={{ pl: 2 }}>
                  <li>Price ranges</li>
                  <li>Cooking methods</li>
                  <li>Portion sizes</li>
                  <li>Pairing suggestions</li>
                </Box>
              </Box>
            </Box>
            <Box mt={3}>
              <Typography variant="body2" color="primary" fontWeight="bold">
                💡 Try asking: "What are your most popular {restaurant.cuisine_type.toLowerCase()} dishes?" or "Do you have any vegetarian options?"
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Restaurant Info Cards */}
      <Box display="flex" gap={3} mb={4} flexWrap="wrap">
        <Box flex={1} minWidth="300px">
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              {restaurant.contact?.phone && (
                <Typography variant="body2" gutterBottom>
                  📞 {restaurant.contact.phone}
                </Typography>
              )}
              {restaurant.contact?.email && (
                <Typography variant="body2" gutterBottom>
                  ✉️ {restaurant.contact.email}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
        
        <Box flex={1} minWidth="300px">
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Location
              </Typography>
              {restaurant.location?.address && (
                <Typography variant="body2" gutterBottom>
                  📍 {restaurant.location.address}
                </Typography>
              )}
              {restaurant.location?.city && restaurant.location?.state && (
                <Typography variant="body2" gutterBottom>
                  {restaurant.location.city}, {restaurant.location.state}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
        
        <Box flex={1} minWidth="300px">
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Menu Stats
              </Typography>
              {restaurant.metadata?.total_menu_items && (
                <Typography variant="body2" gutterBottom>
                  🍽️ {restaurant.metadata.total_menu_items} menu items
                </Typography>
              )}
              <Chip
                label={restaurant.cuisine_type}
                color="primary"
                size="small"
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ChatBot Component */}
      <ChatBot
        restaurant={restaurant}
        isOpen={chatOpen}
        onToggle={() => setChatOpen(!chatOpen)}
      />
    </Container>
  );
};

export default ChatPage; 